import type { Address } from '@ahm/api-wrappers-core';
import { setCookie } from 'cookies-next';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { logger } from './logger';
import { createSelectors } from './selectors';

/**
 * The resolved current city, sourced entirely from the `cities/by-point` API
 * (no hardcoded table). `city_slug` drives URL routing and `city_name` is the
 * display label — both persisted so headers/SSR can read them without a lookup.
 */
export interface CurrentCity {
  id: string;
  slug: string;
  name: string;
}

/**
 * Địa chỉ đến từ URL nhưng KHÔNG dùng được (ngoài vùng phục vụ, hoặc không phân
 * giải được city). Cố tình tách khỏi `address`: commit vào `address` sẽ khiến
 * store query bắn với toạ độ vô nghĩa. Chỉ dùng để prefill input + hiện lỗi
 * inline trong dialog chọn địa chỉ.
 */
export interface PendingLocation {
  /** Có thể thiếu: URL mang địa chỉ nhưng toạ độ hỏng/thiếu vẫn phải prefill được. */
  lat?: number;
  lng?: number;
  label?: string;
  reason: 'not_serviceable' | 'error';
}

export type AiStoreOrderState = {
  isOpenMenu: boolean;
  city_id: string | null;
  city_slug: string | null;
  city_name: string | null;
  address: Address | null;
  /** Transient — KHÔNG persist, chỉ sống trong phiên để dialog đọc. */
  pendingLocation: PendingLocation | null;
  isLoading: boolean;
  skipAutoLocation: boolean; // Flag để tắt auto location
  requireAddressSync: boolean; // Flag để báo cần sync address
};

export interface AiStoreOrderActions {
  setIsOpenMenu: (isOpen: boolean) => void;
  setCityId: (city_id: string) => void;
  /** Set the full resolved city ({id, slug, name}) from the by-point API. */
  setCity: (city: CurrentCity) => void;
  setAddress: (address: Address | null) => void;
  /** Ghi nhận địa chỉ từ URL không dùng được (xem {@link PendingLocation}). */
  setPendingLocation: (pending: PendingLocation | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setSkipAutoLocation: (skip: boolean) => void;
  setRequireAddressSync: (require: boolean) => void;
  syncAddressToCookie: () => void; // Force sync address to cookie
}

const AHAFOOD_STORE_NAME = 'ai.store';

export const ahafoodStore = create<
  AiStoreOrderState & AiStoreOrderActions,
  [
    ['zustand/devtools', AiStoreOrderState & AiStoreOrderActions],
    [
      'zustand/persist',
      Pick<
        AiStoreOrderState,
        'city_id' | 'city_slug' | 'city_name' | 'address'
      >,
    ],
  ]
>(
  logger(
    devtools(
      persist(
        (set, get) => {
          const initialState: AiStoreOrderState = {
            city_id: null,
            city_slug: null,
            city_name: null,
            address: null,
            pendingLocation: null,
            isLoading: false,
            isOpenMenu: false,
            skipAutoLocation: false,
            requireAddressSync: false,
          };

          return {
            ...initialState,
            setIsOpenMenu: (isOpen) => set({ isOpenMenu: isOpen }),
            setIsLoading: (isLoading: boolean) => set({ isLoading }),
            setPendingLocation: (pending: PendingLocation | null) =>
              set({ pendingLocation: pending }),
            setAddress: (address: Address | null) => {
              // Có địa chỉ thật rồi thì pendingLocation hết ý nghĩa.
              set({
                address,
                requireAddressSync: false,
                pendingLocation: null,
              });
              // Save address to cookie for server usage
              if (address) {
                try {
                  void setCookie('ahafood_address', JSON.stringify(address), {
                    maxAge: 30 * 24 * 60 * 60,
                  });
                } catch (e) {
                  // ignore
                }
              }
            },
            setCityId: (city_id: string) => {
              set({ city_id });

              // Save cityId to cookie for server usage
              try {
                void setCookie('ahafood_city_id', city_id, {
                  maxAge: 30 * 24 * 60 * 60, // 30 days
                });
              } catch (e) {
                // ignore
              }
            },
            setCity: (city: CurrentCity) => {
              set({
                city_id: city.id,
                city_slug: city.slug,
                city_name: city.name,
              });

              // Persist the full resolved city to cookies so the server (SSR
              // redirects, [city] pages) can read slug/name without a lookup.
              try {
                const maxAge = 30 * 24 * 60 * 60; // 30 days
                void setCookie('ahafood_city_id', city.id, { maxAge });
                void setCookie('ahafood_city_slug', city.slug, { maxAge });
                void setCookie('ahafood_city_name', city.name, { maxAge });
              } catch (e) {
                // ignore
              }
            },
            setSkipAutoLocation: (skip: boolean) =>
              set({ skipAutoLocation: skip }),
            setRequireAddressSync: (require: boolean) =>
              set({ requireAddressSync: require }),
            syncAddressToCookie: () => {
              const state = get();
              if (state.address) {
                try {
                  void setCookie(
                    'ahafood_address',
                    JSON.stringify(state.address),
                    {
                      maxAge: 30 * 24 * 60 * 60,
                    }
                  );
                  set({ requireAddressSync: false });
                } catch (e) {
                  console.error('Failed to sync address to cookie:', e);
                }
              }
            },
          };
        },
        {
          name: AHAFOOD_STORE_NAME,
          storage: createJSONStorage(() => localStorage),
          partialize: (state) => ({
            address: state.address,
            city_id: state.city_id,
            city_slug: state.city_slug,
            city_name: state.city_name,
          }),
        }
      )
    )
  )
);

export const useAiFoodOrderStore = createSelectors(ahafoodStore);
